import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getAvailableVaults, VaultSummary, Vault, LinkVaultOptions, getVaultsByCompany } from "../../utils/api/endpoints/vault";
import AccessPasswordModal from "./AccessPasswordModal";
import useUserCredentials from "../../utils/hooks/user/useUserCredentials";
import { UserRole } from "../../utils/api/endpoints/user/credentials";

// Type for vault configuration that will be stored before program creation
export interface VaultLinkConfig {
  vaultId: number;
  vaultName: string;
  options: LinkVaultOptions;
}

interface VaultPreSelectorProps {
  selectedVaultIds: number[];
  onSelectionChange: (vaultIds: number[]) => void;
  // New prop to pass vault configurations with access passwords
  vaultConfigs?: VaultLinkConfig[];
  onVaultConfigChange?: (configs: VaultLinkConfig[]) => void;
}

/**
 * VaultPreSelector - For selecting vaults BEFORE a program is created
 * Now includes Link button to configure access password before program creation
 */
const VaultPreSelector: React.FC<VaultPreSelectorProps> = ({
  selectedVaultIds,
  onSelectionChange,
  vaultConfigs = [],
  onVaultConfigChange
}) => {
  const { role } = useUserCredentials();

  // Check if user is a sub-user (cannot link vaults - requires master password)
  const isSubUser = role === UserRole.DEVELOPER ||
                    role === UserRole.BUSINESS_MANAGER;

  const [availableVaults, setAvailableVaults] = useState<VaultSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Access password modal state
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [selectedVault, setSelectedVault] = useState<Vault | null>(null);
  const [isLoadingVault, setIsLoadingVault] = useState(false);
  const [loadingVaultId, setLoadingVaultId] = useState<number | null>(null);

  useEffect(() => {
    const fetchVaults = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await getAvailableVaults();
        setAvailableVaults(res.vaults || []);
      } catch (err: any) {
        setError(err.message || "Failed to load vaults");
      } finally {
        setIsLoading(false);
      }
    };
    fetchVaults();
  }, []);

  // Handle clicking the Link button - opens access password modal
  const handleLinkVault = async (vaultId: number) => {
    setIsLoadingVault(true);
    setLoadingVaultId(vaultId);

    try {
      // Fetch full vault with entries for re-encryption
      const response = await getVaultsByCompany();
      const fullVault = response.vaults.find((v: Vault) => v.vault_id === vaultId);

      if (!fullVault) {
        throw new Error("Vault not found");
      }

      setSelectedVault(fullVault);
      setShowAccessModal(true);
    } catch (err: any) {
      console.error("Error loading vault:", err);
      setError(err.message || "Failed to load vault");
    } finally {
      setIsLoadingVault(false);
      setLoadingVaultId(null);
    }
  };

  // Handle access password submission - stores config for later linking
  const handleAccessPasswordSubmit = async (options: LinkVaultOptions) => {
    if (!selectedVault) return;

    // Store the vault configuration
    const newConfig: VaultLinkConfig = {
      vaultId: selectedVault.vault_id,
      vaultName: selectedVault.name,
      options
    };

    // Update vault configs
    if (onVaultConfigChange) {
      const existingConfigs = vaultConfigs.filter(c => c.vaultId !== selectedVault.vault_id);
      onVaultConfigChange([...existingConfigs, newConfig]);
    }

    // Add to selected vault IDs
    if (!selectedVaultIds.includes(selectedVault.vault_id)) {
      onSelectionChange([...selectedVaultIds, selectedVault.vault_id]);
    }

    setShowAccessModal(false);
    setSelectedVault(null);
  };

  // Handle unlinking a vault
  const handleUnlinkVault = (vaultId: number) => {
    onSelectionChange(selectedVaultIds.filter((id) => id !== vaultId));
    if (onVaultConfigChange) {
      onVaultConfigChange(vaultConfigs.filter(c => c.vaultId !== vaultId));
    }
  };

  // Check if a vault has been configured (has access password set)
  const isVaultConfigured = (vaultId: number) => {
    return vaultConfigs.some(c => c.vaultId === vaultId);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-sm text-gray-500">Loading vaults...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <p className="text-sm text-red-600">{error}</p>
      </div>
    );
  }

  // Sub-users cannot link vaults - show info message
  if (isSubUser) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-6">
        <div className="flex flex-col items-center justify-center text-center">
          <div className="rounded-full bg-amber-100 p-3 mb-3">
            <svg className="h-6 w-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-sm font-semibold text-slate-800 mb-1">Credential Linking Restricted</h3>
          <p className="text-xs text-slate-600 max-w-md mb-2">
            Please contact your company's administrator to link credential vaults to this program.
          </p>
          <p className="text-xs text-slate-500 max-w-md">
            Only the Business Owner or Organisation Admin can link credentials by editing the program after it's created.
          </p>
        </div>
      </div>
    );
  }

  if (availableVaults.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6">
        <div className="flex flex-col items-center justify-center text-center">
          <div className="rounded-full bg-blue-100 p-3 mb-3">
            <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h3 className="text-sm font-semibold text-slate-800 mb-1">No Credentials Available</h3>
          <p className="text-xs text-slate-500 max-w-md mb-3">
            Create credentials in the Credential Vault first to link them to programs.
          </p>
          <Link
            to="/dashboard/vault"
            target="_blank"
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create Credentials
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-slate-500">
          Select credentials to share with researchers for this program
        </p>
        <Link
          to="/dashboard/vault"
          target="_blank"
          className="text-xs text-blue-600 hover:text-blue-700 hover:underline"
        >
          Manage Vault →
        </Link>
      </div>

      <div className="space-y-2 max-h-64 overflow-y-auto">
        {availableVaults.map((vault) => {
          const isLinked = selectedVaultIds.includes(vault.vault_id);
          const isConfigured = isVaultConfigured(vault.vault_id);
          const isCurrentlyLoading = loadingVaultId === vault.vault_id;

          return (
            <div
              key={vault.vault_id}
              className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                isLinked
                  ? "border-blue-200 bg-blue-50"
                  : "border-gray-200 bg-white hover:border-gray-300"
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`relative p-2 rounded-lg ${isLinked ? "bg-blue-100" : "bg-gray-100"}`}>
                  <svg className={`w-4 h-4 ${isLinked ? "text-blue-600" : "text-gray-500"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                  </svg>
                  {isConfigured && (
                    <div className="absolute -top-1 -right-1 bg-green-500 rounded-full p-0.5">
                      <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
                <div>
                  <h4 className="text-sm font-medium text-slate-800">{vault.name}</h4>
                  {vault.description && (
                    <p className="text-xs text-slate-500 line-clamp-1">{vault.description}</p>
                  )}
                </div>
              </div>

              <button
                type="button"
                onClick={() => isLinked ? handleUnlinkVault(vault.vault_id) : handleLinkVault(vault.vault_id)}
                disabled={isCurrentlyLoading || isLoadingVault}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  isCurrentlyLoading || isLoadingVault
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : isLinked
                      ? "bg-red-100 text-red-600 hover:bg-red-200"
                      : "bg-blue-100 text-blue-600 hover:bg-blue-200"
                }`}
              >
                {isCurrentlyLoading ? (
                  <span className="flex items-center gap-1">
                    <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    ...
                  </span>
                ) : isLinked ? (
                  "Unlink"
                ) : (
                  "Link"
                )}
              </button>
            </div>
          );
        })}
      </div>

      {selectedVaultIds.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <p className="text-xs text-green-600 flex items-center gap-1">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            {selectedVaultIds.length} credential{selectedVaultIds.length !== 1 ? "s" : ""} will be linked to this program
          </p>
        </div>
      )}

      {/* Access Password Modal */}
      {selectedVault && (
        <AccessPasswordModal
          isOpen={showAccessModal}
          onClose={() => {
            setShowAccessModal(false);
            setSelectedVault(null);
          }}
          onSubmit={handleAccessPasswordSubmit}
          vault={selectedVault}
        />
      )}
    </div>
  );
};

export default VaultPreSelector;

